import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ComicCard from '@/components/feature/ComicCard';
import { comics, categories, siteInfo } from '@/mocks/comics';
import { useState, useMemo } from 'react';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState(slug || 'hanman');

  const currentCategory = categories.find((c) => c.slug === activeTab);
  const categoryComics = useMemo(
    () => comics.filter((c) => c.category === activeTab),
    [activeTab]
  );

  const categoryLabels: Record<string, string> = {
    hanman: '韩漫',
    riman: '日漫',
    guoman: '国漫',
  };

  const handleTabChange = (newSlug: string) => {
    setActiveTab(newSlug);
    window.scrollTo({ top: 0, behavior: 'instant' });
    window.history.replaceState(null, '', `/category/${newSlug}`);
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `女神漫画 - ${currentCategory?.name || '分类'}`,
    description: currentCategory?.description,
    url: `${siteInfo.url}/category/${activeTab}`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: siteInfo.url,
    },
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: categoryComics.map((comic, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'CreativeWork',
          name: comic.title,
          url: `${siteInfo.url}/comic/${comic.id}`,
        },
      })),
    },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webpageSchema) }}
      />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: currentCategory?.name || '分类' },
            ]}
          />

          <section className="pb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                  女神漫画 - {currentCategory?.name}
                </h1>
                <p className="text-sm text-foreground-500">{currentCategory?.description}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-8 px-1 py-1 bg-background-100 rounded-full w-fit">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleTabChange(cat.slug)}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    activeTab === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
              {categoryComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>

            {categoryComics.length === 0 && (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-book-open-line text-3xl text-foreground-300"></i>
                </div>
                <p className="text-foreground-500">该分类暂无漫画</p>
              </div>
            )}
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}